import React, { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Edit, Flame, ClipboardList, ChevronDown, ChevronUp, Play, CheckCircle, Loader2, RefreshCw, ArrowRight, TrendingUp, BarChart2, Columns3, Fuel, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { SearchableSelect } from "../components/ui/searchable-select";
import { toast } from "sonner";
import api from "../utils/api";
import { formatDate } from "../utils/format";

const BATCH_STATUSES = [
  { value: "programado", label: "Programado", color: "bg-blue-100 text-blue-700", border: "border-blue-200" },
  { value: "en_horno", label: "En horno", color: "bg-amber-100 text-amber-700", border: "border-amber-200" },
  { value: "enfriando", label: "Enfriando", color: "bg-cyan-100 text-cyan-700", border: "border-cyan-200" },
  { value: "completado", label: "Completado", color: "bg-emerald-100 text-emerald-700", border: "border-emerald-200" },
  { value: "cancelado", label: "Cancelado", color: "bg-red-100 text-red-700", border: "border-red-200" },
];
function batchStatusColor(s) { return BATCH_STATUSES.find((b) => b.value === s)?.color || "bg-slate-100 text-slate-600"; }
function batchStatusLabel(s) { return BATCH_STATUSES.find((b) => b.value === s)?.label || s; }
function batchBorderColor(s) { return BATCH_STATUSES.find((b) => b.value === s)?.border || "border-slate-200"; }

// ─── OVEN FORM ─────────────────────────────────────────────────────────────
function OvenForm({ oven, products, onClose, onSave }) {
  const [form, setForm] = useState({
    name: "", type: "grande", location: "principal",
    product_capacities: [], restricted_products: [], notes: "",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (oven) setForm({ ...oven }); }, [oven]);

  const addCapacity = () => setForm((f) => ({
    ...f, product_capacities: [...f.product_capacities, { product_id: "", product_name: "", capacity_kg: 0 }]
  }));
  const updateCapacity = (i, field, value) => {
    const caps = [...form.product_capacities];
    if (field === "product_id") {
      const prod = products.find((p) => p.id === value);
      caps[i] = { ...caps[i], product_id: value, product_name: prod?.name || "" };
    } else caps[i] = { ...caps[i], [field]: field === "capacity_kg" ? Number(value) : value };
    setForm((f) => ({ ...f, product_capacities: caps }));
  };
  const removeCapacity = (i) => setForm((f) => ({ ...f, product_capacities: f.product_capacities.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.name) { toast.error("Nombre requerido"); return; }
    setSaving(true);
    try {
      if (oven) { await api.put(`/ovens/${oven.id}`, form); toast.success("Horno actualizado"); }
      else { await api.post("/ovens", form); toast.success("Horno creado"); }
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const finalProducts = products.filter((p) => p.category === "producto_final").sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="oven-form">
        <DialogHeader><DialogTitle>{oven ? "Editar Horno" : "Nuevo Horno"}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Nombre *</label>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Horno Grande 1" data-testid="oven-name-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Tipo</label>
              <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="oven-type-select">
                <option value="grande">Grande</option>
                <option value="chico">Chico</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Ubicacion</label>
              <input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="principal" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-700">Capacidades por producto (kg crudo)</label>
              <button onClick={addCapacity} className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1">
                <Plus size={12} /> Agregar
              </button>
            </div>
            {form.product_capacities.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Sin capacidades configuradas</p>
            ) : (
              <div className="space-y-2">
                {form.product_capacities.map((cap, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="flex-1">
                      <SearchableSelect options={finalProducts.map((p) => ({ value: p.id, label: p.name }))}
                        value={cap.product_id} onValueChange={(v) => updateCapacity(i, "product_id", v)}
                        placeholder="Producto..." searchPlaceholder="Buscar producto..." />
                    </div>
                    <input type="number" value={cap.capacity_kg} onChange={(e) => updateCapacity(i, "capacity_kg", e.target.value)}
                      className="w-24 border border-slate-200 rounded-lg px-2 py-2 text-sm text-right" min="0" step="0.1" placeholder="kg" />
                    <span className="text-xs text-slate-500">kg</span>
                    <button onClick={() => removeCapacity(i)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-400" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notas</label>
            <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Ej: Solo para pulmon y oreja" />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
            <button onClick={handleSave} disabled={saving} data-testid="save-oven-btn"
              className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium disabled:opacity-60">
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── GAS CYLINDER FORM ─────────────────────────────────────────────────────
function GasCylinderForm({ cylinder, ovens, onClose, onSave }) {
  const [form, setForm] = useState({
    name: "", oven_id: "", oven_name: "", purchase_date: "",
    tare_weight: "", gross_weight: "", cost_per_kg: "",
    start_date: "", notes: "", status: "lleno",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (cylinder) setForm({
      name: cylinder.name || "", oven_id: cylinder.oven_id || "", oven_name: cylinder.oven_name || "",
      purchase_date: cylinder.purchase_date || "",
      tare_weight: cylinder.tare_weight || "", gross_weight: cylinder.gross_weight || "",
      cost_per_kg: cylinder.cost_per_kg || cylinder.cost_per_liter || "",
      start_date: cylinder.start_date || "", notes: cylinder.notes || "",
      status: cylinder.status || "lleno",
    });
  }, [cylinder]);

  const netGas = (Number(form.gross_weight) || 0) - (Number(form.tare_weight) || 0);
  const totalCost = netGas > 0 ? netGas * (Number(form.cost_per_kg) || 0) : 0;

  const handleSave = async () => {
    if (!form.oven_id) { toast.error("Selecciona un horno"); return; }
    if (!form.tare_weight || !form.gross_weight) { toast.error("Indica tara y peso bruto"); return; }
    if (netGas <= 0) { toast.error("Peso bruto debe ser mayor que la tara"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form, name: form.name || `Cilindro ${form.purchase_date || ""}`,
        tare_weight: Number(form.tare_weight) || 0,
        gross_weight: Number(form.gross_weight) || 0,
        cost_per_kg: Number(form.cost_per_kg) || 0,
      };
      if (cylinder) { await api.put(`/gas-cylinders/${cylinder.id}`, payload); toast.success("Cilindro actualizado"); }
      else { await api.post("/gas-cylinders", payload); toast.success("Cilindro registrado"); }
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="gas-cylinder-form">
        <DialogHeader><DialogTitle>{cylinder ? "Editar Cilindro" : "Nuevo Cilindro de Gas"}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Nombre / Etiqueta</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Ej: Cilindro #3" data-testid="cyl-name" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Horno *</label>
              <select value={form.oven_id} onChange={e => {
                const oven = ovens.find(o => o.id === e.target.value);
                setForm(f => ({ ...f, oven_id: e.target.value, oven_name: oven?.name || "" }));
              }} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="cyl-oven-select">
                <option value="">Seleccionar horno...</option>
                {ovens.map(o => <option key={o.id} value={o.id}>{o.name} ({o.type})</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Tara (kg) *</label>
              <input type="number" value={form.tare_weight} onChange={e => setForm(f => ({ ...f, tare_weight: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Peso vacio" min="0" step="0.1" data-testid="cyl-tare" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Peso bruto (kg) *</label>
              <input type="number" value={form.gross_weight} onChange={e => setForm(f => ({ ...f, gross_weight: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Tara + gas" min="0" step="0.1" data-testid="cyl-gross" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">$/kg gas</label>
              <input type="number" value={form.cost_per_kg} onChange={e => setForm(f => ({ ...f, cost_per_kg: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Ej: 22.50" min="0" step="0.01" data-testid="cyl-cost-kg" />
            </div>
          </div>
          {netGas > 0 && (
            <div className="bg-teal-50 rounded-lg p-2.5 grid grid-cols-2 gap-2 text-center">
              <div>
                <span className="text-[10px] text-teal-600">Gas neto</span>
                <div className="text-sm font-bold text-teal-800">{netGas.toFixed(2)} kg</div>
              </div>
              <div>
                <span className="text-[10px] text-teal-600">Costo total</span>
                <div className="text-sm font-bold text-teal-800">${totalCost.toFixed(2)}</div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Fecha compra</label>
              <input type="date" value={form.purchase_date} onChange={e => setForm(f => ({ ...f, purchase_date: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="cyl-purchase-date" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Inicio uso</label>
              <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="cyl-start-date" />
            </div>
          </div>
          {cylinder && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Estado</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="cyl-status">
                <option value="lleno">Lleno</option>
                <option value="en_uso">En uso</option>
                <option value="agotado">Agotado</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notas</label>
            <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Observaciones..." />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
            <button onClick={handleSave} disabled={saving} data-testid="save-cylinder-btn"
              className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium disabled:opacity-60">
              {saving ? "Guardando..." : cylinder ? "Actualizar" : "Registrar Cilindro"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─── FULFILLABLE ORDERS PANEL ──────────────────────────────────────────────
function FulfillableOrdersPanel({ orders, onMarkReady, onSkip, marking }) {
  const [selected, setSelected] = useState(orders.map(o => o.sale_id));
  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  return (
    <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="fulfillable-orders-panel">
      <h4 className="text-sm font-bold text-emerald-800 mb-2 flex items-center gap-2">
        <CheckCircle size={16} /> Pedidos listos para surtir ({orders.length})
      </h4>
      <p className="text-xs text-emerald-700 mb-3">Con el stock actual, estos pedidos tienen todo el producto disponible.</p>
      <div className="space-y-1.5 mb-3 max-h-40 overflow-y-auto">
        {orders.map(o => (
          <label key={o.sale_id} className="flex items-center gap-2 text-xs bg-white rounded border border-emerald-100 px-3 py-2 cursor-pointer hover:bg-emerald-50">
            <input type="checkbox" checked={selected.includes(o.sale_id)} onChange={() => toggle(o.sale_id)} className="rounded border-emerald-300" />
            <span className="font-medium text-slate-800">{o.folio}</span>
            <span className="text-slate-500">- {o.customer_name}</span>
            <span className="ml-auto text-emerald-700 font-medium">${(o.total || 0).toLocaleString()}</span>
          </label>
        ))}
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onSkip} className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg">Omitir</button>
        <button onClick={() => onMarkReady(selected)} disabled={marking || selected.length === 0}
          data-testid="mark-ready-btn"
          className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-medium disabled:opacity-60">
          {marking ? "Marcando..." : `Marcar ${selected.length} como "Listo para Surtir"`}
        </button>
      </div>
    </div>
  );
}

// ─── BATCH FORM ────────────────────────────────────────────────────────────
function BatchForm({ batch, ovens, products, activeSales, prefill, onClose, onSave }) {
  const emptyItem = () => ({ product_id: "", product_name: "", input_qty: 0, input_unit: "kg", output_unit: "kg", estimated_output_qty: 0, actual_output_qty: "", hours_in_oven: 0, conversion: null, raw_material_cost: 0 });
  const [form, setForm] = useState({
    oven_id: "", oven_name: "",
    items: [emptyItem()],
    batch_costs: { additional_materials: [], production_costs: [] },
    priority_orders: [], notes: "", status: "programado",
    flame_start: "", flame_end: "", turbine_end: "",
  });
  const [saving, setSaving] = useState(false);
  const [showCosts, setShowCosts] = useState(false);
  const [gasCylinders, setGasCylinders] = useState([]);
  const [cylinderUsages, setCylinderUsages] = useState([]);
  const [addingCylUsage, setAddingCylUsage] = useState(false);
  const [cylUsageForm, setCylUsageForm] = useState({ cylinder_id: "", weight_before: "", weight_after: "" });
  const [receivingLots, setReceivingLots] = useState([]);
  const [availableReceivings, setAvailableReceivings] = useState([]);

  useEffect(() => {
    if (batch) {
      const items = batch.batch_items?.length > 0 ? batch.batch_items.map(i => ({ ...i, actual_output_qty: i.actual_output_qty ?? "" })) : [{
        product_id: batch.product_id || "", product_name: batch.product_name || "",
        input_qty: batch.input_qty || batch.raw_kg || 0, input_unit: batch.input_unit || "kg",
        output_unit: batch.output_unit || batch.input_unit || "kg",
        estimated_output_qty: batch.estimated_output_qty || batch.estimated_output_kg || 0,
        actual_output_qty: batch.actual_output_qty ?? batch.actual_output_kg ?? "",
        hours_in_oven: 0, conversion: batch.conversion || null,
        raw_material_cost: batch.batch_costs?.raw_material_total || 0,
      }];
      const rawCosts = batch.batch_costs || {};
      // Convert old object format {gas: 5, electricity: 10} to new array format [{name, hours, cost_per_hour, total}]
      let prodCosts = rawCosts.production_costs || [];
      if (prodCosts && !Array.isArray(prodCosts)) {
        const oldLabels = { gas: "Gas", electricity: "Electricidad", water: "Agua", labor: "Mano de obra", packaging: "Empaque", other: "Otros" };
        prodCosts = Object.entries(prodCosts).filter(([, v]) => v > 0).map(([k, v]) => ({ name: oldLabels[k] || k, hours: 0, cost_per_hour: 0, total: Number(v) || 0 }));
      }
      setForm({
        oven_id: batch.oven_id || "", oven_name: batch.oven_name || "", items,
        batch_costs: { additional_materials: rawCosts.additional_materials || [], production_costs: prodCosts },
        priority_orders: batch.priority_orders || [], notes: batch.notes || "", status: batch.status || "programado",
        flame_start: batch.flame_start || "", flame_end: batch.flame_end || "", turbine_end: batch.turbine_end || "",
      });
      // Load cylinder usages for this batch
      api.get(`/production/batches/${batch.id}/cylinder-usage`).then(r => setCylinderUsages(r.data?.usages || [])).catch(() => {});
      setReceivingLots(batch.receiving_lots || []);
      const bc = batch.batch_costs || {};
      const pcRaw = bc.production_costs;
      const hasProdCosts = Array.isArray(pcRaw) ? pcRaw.length > 0 : (pcRaw && Object.values(pcRaw).some(v => v > 0));
      if (bc.raw_material_total > 0 || (bc.additional_materials || []).length > 0 || hasProdCosts) setShowCosts(true);
      return;
    }
    if (prefill?.product_id) {
      const prod = products.find(p => p.id === prefill.product_id);
      if (prod) {
        const isConteo = (prod.yield_type || "peso") === "conteo";
        const unit = isConteo ? "pza" : (prod.purchase_unit || "kg");
        setForm(f => ({ ...f, items: [{ ...emptyItem(), product_id: prod.id, product_name: prod.name, input_unit: unit, output_unit: unit }] }));
      }
    }
  }, [batch, prefill, products]);

  // Load gas cylinders for selected oven
  useEffect(() => {
    if (form.oven_id) {
      api.get(`/gas-cylinders/by-oven/${form.oven_id}`).then(r => setGasCylinders(r.data)).catch(() => {});
    } else {
      api.get("/gas-cylinders").then(r => setGasCylinders(r.data)).catch(() => {});
    }
  }, [form.oven_id]);

  // Helper functions to refresh batch data and cylinder usages
  const loadBatch = async (batchId) => {
    try {
      const res = await api.get(`/batches/${batchId}`);
      if (res.data) {
        // Update batch reference without resetting form
        if (batch) Object.assign(batch, res.data);
      }
    } catch {}
  };
  const loadCylinderUsages = async (batchId) => {
    try {
      const res = await api.get(`/production/batches/${batchId}/cylinder-usage`);
      setCylinderUsages(res.data?.usages || []);
    } catch {}
  };

  // Load available receivings for traceability
  useEffect(() => {
    api.get("/receivings").then(r => setAvailableReceivings(r.data || [])).catch(() => {});
  }, []);

  // Auto-suggest receivings when product changes (filter by ingredient product_ids)
  const getSuggestedReceivings = () => {
    const selectedProductIds = form.items.map(it => it.product_id).filter(Boolean);
    if (selectedProductIds.length === 0) return availableReceivings;
    // Get ingredient product_ids for selected products
    const ingredientIds = new Set();
    selectedProductIds.forEach(pid => {
      const prod = products.find(p => p.id === pid);
      if (prod?.ingredients) {
        prod.ingredients.forEach(ing => { if (ing.product_id) ingredientIds.add(ing.product_id); });
      }
    });
    if (ingredientIds.size === 0) return availableReceivings;
    // Sort: matching receivings first, then the rest
    const matching = availableReceivings.filter(r => ingredientIds.has(r.product_id));
    const others = availableReceivings.filter(r => !ingredientIds.has(r.product_id));
    return [...matching, ...others];
  };
  const suggestedReceivings = getSuggestedReceivings();
  const matchingIngredientIds = new Set();
  form.items.forEach(it => {
    const prod = products.find(p => p.id === it.product_id);
    if (prod?.ingredients) prod.ingredients.forEach(ing => { if (ing.product_id) matchingIngredientIds.add(ing.product_id); });
  });

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) => { if (form.items.length > 1) setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) })); };
  const updateItem = (idx, updates) => setForm(f => {
    const items = [...f.items]; items[idx] = { ...items[idx], ...updates }; return { ...f, items };
  });

  const selectProductForItem = (idx, productId) => {
    const prod = products.find(p => p.id === productId);
    if (!prod) return;
    const isConteo = (prod.yield_type || "peso") === "conteo";
    const inUnit = isConteo ? "pza" : (prod.purchase_unit || "kg");
    const outUnit = isConteo ? "pza" : (prod.purchase_unit || "kg");
    let inputQty = form.items[idx].input_qty;
    if (form.oven_id) {
      const oven = ovens.find(o => o.id === form.oven_id);
      const cap = (oven?.product_capacities || []).find(c => c.product_id === productId);
      if (cap) inputQty = cap.capacity_kg;
    }
    const yt = prod.yield_type || "peso"; const yp = prod.yield_pct || 0;
    let est = 0;
    if (yt === "conteo") est = inputQty;
    else if (yp > 0 && inputQty > 0) est = Math.round(inputQty * (yp / 100) * 100) / 100;
    const ovenHours = prod.typical_oven_hours || 0;
    updateItem(idx, { product_id: productId, product_name: prod.name, input_qty: inputQty, input_unit: inUnit, output_unit: outUnit, estimated_output_qty: est, hours_in_oven: ovenHours || form.items[idx].hours_in_oven || 0 });

    // Auto-fill batch production costs from product template
    const tbc = prod.typical_batch_costs || [];
    if (tbc.length > 0) {
      setShowCosts(true);
      setForm(f => {
        const existing = f.batch_costs?.production_costs || [];
        if (existing.length === 0) {
          // First product with template: fill all costs
          const maxHours = Math.max(ovenHours, ...f.items.filter((_, i) => i !== idx).map(it => it.hours_in_oven || 0));
          const costs = tbc.map(c => ({ name: c.name, hours: maxHours || c.hours, cost_per_hour: c.cost_per_hour || 0, total: (maxHours || c.hours) * (c.cost_per_hour || 0) }));
          return { ...f, batch_costs: { ...f.batch_costs, production_costs: costs } };
        } else {
          // Batch already has costs: update hours to max of all products
          const allHours = f.items.map((it, i) => i === idx ? ovenHours : (it.hours_in_oven || 0));
          const maxH = Math.max(...allHours);
          if (maxH > 0) {
            const updated = existing.map(c => {
              if (c.cost_per_hour > 0) return { ...c, hours: maxH, total: maxH * c.cost_per_hour };
              return c;
            });
            return { ...f, batch_costs: { ...f.batch_costs, production_costs: updated } };
          }
          return f;
        }
      });
    }
  };

  const updateItemQty = (idx, val) => {
    const qty = Number(val) || 0;
    const item = form.items[idx];
    const prod = products.find(p => p.id === item.product_id);
    const yt = prod?.yield_type || "peso"; const yp = prod?.yield_pct || 0;
    let est = item.estimated_output_qty;
    if (yt === "conteo") est = qty;
    else if (yp > 0) est = Math.round(qty * (yp / 100) * 100) / 100;
    updateItem(idx, { input_qty: qty, estimated_output_qty: est });
  };

  const updateConversion = (idx, field, value) => {
    const item = form.items[idx];
    const prod = products.find(p => p.id === item.product_id);
    const conv = item.conversion || { purchased_qty: 0, purchased_unit: item.input_unit, purchase_unit_cost: 0, converted_qty: 0, converted_unit: prod?.unit || "" };
    const updated = { ...conv, [field]: Number(value) || 0 };
    updated.purchase_total = (updated.purchased_qty || 0) * (updated.purchase_unit_cost || 0);
    updated.cost_per_converted = updated.converted_qty > 0 ? updated.purchase_total / updated.converted_qty : 0;
    updateItem(idx, { conversion: updated, raw_material_cost: updated.purchase_total });
  };

  const selectOven = (ovenId) => {
    const oven = ovens.find(o => o.id === ovenId);
    if (!oven) return;
    setForm(f => {
      const items = f.items.map(item => {
        if (!item.product_id) return item;
        const cap = (oven.product_capacities || []).find(c => c.product_id === item.product_id);
        if (!cap) return item;
        const prod = products.find(p => p.id === item.product_id);
        const yt = prod?.yield_type || "peso"; const yp = prod?.yield_pct || 0;
        const raw = cap.capacity_kg;
        let est = item.estimated_output_qty;
        if (yt === "conteo") est = raw; else if (yp > 0) est = Math.round(raw * (yp / 100) * 100) / 100;
        return { ...item, input_qty: raw, estimated_output_qty: est };
      });
      return { ...f, oven_id: ovenId, oven_name: oven.name, items };
    });
  };

  const toggleOrder = (saleId) => setForm(f => ({
    ...f, priority_orders: f.priority_orders.includes(saleId)
      ? f.priority_orders.filter(id => id !== saleId) : [...f.priority_orders, saleId]
  }));

  const addProdCost = () => setForm(f => ({
    ...f, batch_costs: { ...f.batch_costs, production_costs: [...(f.batch_costs?.production_costs || []), { name: "", hours: 0, cost_per_hour: 0, total: 0 }] }
  }));
  const updateProdCost = (i, field, value) => setForm(f => {
    const costs = [...(f.batch_costs?.production_costs || [])];
    costs[i] = { ...costs[i], [field]: field === "name" ? value : Number(value) || 0 };
    costs[i].total = (costs[i].hours || 0) * (costs[i].cost_per_hour || 0);
    // Allow direct total entry if hours/rate not used
    if (field === "total") costs[i].total = Number(value) || 0;
    return { ...f, batch_costs: { ...f.batch_costs, production_costs: costs } };
  });
  const removeProdCost = (i) => setForm(f => ({
    ...f, batch_costs: { ...f.batch_costs, production_costs: (f.batch_costs?.production_costs || []).filter((_, idx) => idx !== i) }
  }));
  const addMaterial = () => setForm(f => ({
    ...f, batch_costs: { ...f.batch_costs, additional_materials: [...(f.batch_costs?.additional_materials || []), { name: "", qty: 0, unit: "lt", unit_cost: 0, total_cost: 0 }] }
  }));
  const updateMaterial = (i, field, value) => setForm(f => {
    const mats = [...(f.batch_costs?.additional_materials || [])];
    mats[i] = { ...mats[i], [field]: field === "name" || field === "unit" ? value : Number(value) || 0 };
    mats[i].total_cost = (mats[i].qty || 0) * (mats[i].unit_cost || 0);
    return { ...f, batch_costs: { ...f.batch_costs, additional_materials: mats } };
  });
  const removeMaterial = (i) => setForm(f => ({
    ...f, batch_costs: { ...f.batch_costs, additional_materials: (f.batch_costs?.additional_materials || []).filter((_, idx) => idx !== i) }
  }));

  const calcSharedCost = () => {
    const bc = form.batch_costs || {};
    const additional = (bc.additional_materials || []).reduce((s, m) => s + (m.total_cost || 0), 0);
    const pc = bc.production_costs || [];
    const prodTotal = Array.isArray(pc) ? pc.reduce((s, c) => s + (c.total || 0), 0) : Object.values(pc).reduce((s, v) => s + (Number(v) || 0), 0);
    return additional + prodTotal;
  };

  const handleSave = async () => {
    if (form.items.some(it => !it.product_id)) { toast.error("Selecciona producto para cada linea"); return; }
    if (!form.oven_id) { toast.error("Selecciona un horno"); return; }
    setSaving(true);
    try {
      const payload = {
        oven_id: form.oven_id, oven_name: form.oven_name,
        batch_items: form.items,
        product_id: form.items[0].product_id, product_name: form.items.map(i => i.product_name).join(", "),
        raw_kg: form.items.reduce((s, it) => s + (it.input_qty || 0), 0),
        estimated_output_kg: form.items[0].estimated_output_qty || 0,
        batch_costs: form.batch_costs, priority_orders: form.priority_orders,
        receiving_lots: receivingLots,
        notes: form.notes, status: form.status, is_mixed: form.items.length > 1,
        flame_start: form.flame_start || null, flame_end: form.flame_end || null,
        turbine_end: form.turbine_end || null,
      };
      let res;
      if (batch) {
        res = await api.put(`/production/batches/${batch.id}`, payload);
        toast.success("Lote actualizado");
      } else {
        res = await api.post("/production/batches", payload);
        toast.success("Lote programado");
      }
      // Check for fulfillable orders after batch completion
      const fulfillable = res?.data?.fulfillable_orders || [];
      if (fulfillable.length > 0) {
        setFulfillableOrders(fulfillable);
      } else {
        onSave();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const [fulfillableOrders, setFulfillableOrders] = useState([]);
  const [markingReady, setMarkingReady] = useState(false);

  const handleMarkReady = async (selectedIds) => {
    if (selectedIds.length === 0) { onSave(); return; }
    setMarkingReady(true);
    try {
      await api.post("/sales/mark-ready", { sale_ids: selectedIds });
      toast.success(`${selectedIds.length} pedido(s) marcados como "Listo para Surtir"`);
    } catch (err) { toast.error("Error al marcar pedidos"); }
    finally { setMarkingReady(false); onSave(); }
  };

  const finalProducts = products.filter(p => p.category === "producto_final").sort((a, b) => a.name.localeCompare(b.name));
  const allProductIds = form.items.map(i => i.product_id).filter(Boolean);
  const relevantSales = allProductIds.length > 0 ? (activeSales || []).filter(s =>
    (s.items || []).some(i => allProductIds.includes(i.product_id))) : [];
  const totalHours = form.items.reduce((s, it) => s + (it.hours_in_oven || 0), 0);
  const sharedCost = calcSharedCost();
  const totalRawCost = form.items.reduce((s, it) => s + (it.raw_material_cost || 0), 0);
  const totalCost = totalRawCost + sharedCost;

  // Overlap-based cost allocation for mixed batches
  // If tendon=28h and pezuña=50h: 0-28h shared, 28-50h only pezuña
  const calcOverlapAllocations = () => {
    const items = form.items;
    const n = items.length;
    const allocations = items.map(() => 0);
    if (n <= 1 || sharedCost <= 0) {
      return allocations.map(() => sharedCost);
    }
    // Sort items by hours ascending, keep original index
    const sorted = items.map((it, i) => ({ idx: i, hours: it.hours_in_oven || 0 }))
      .sort((a, b) => a.hours - b.hours);
    const maxHours = sorted[sorted.length - 1].hours;
    if (maxHours <= 0) return allocations.map(() => sharedCost / n);

    const costPerHour = sharedCost / maxHours;
    let prevTime = 0;
    for (let seg = 0; seg < n; seg++) {
      const exitTime = sorted[seg].hours;
      if (exitTime <= prevTime) continue;
      const segDuration = exitTime - prevTime;
      const activeCount = n - seg; // products still in oven
      const perProduct = (segDuration * costPerHour) / activeCount;
      for (let j = seg; j < n; j++) {
        allocations[sorted[j].idx] += perProduct;
      }
      prevTime = exitTime;
    }
    return allocations;
  };
  const overlapAllocations = (form.items.length > 1 && totalHours > 0) ? calcOverlapAllocations() : null;
  const MATERIAL_UNITS = ["lt", "ml", "kg", "g", "galon", "pza", "bolsa"];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="batch-form">
        <DialogHeader><DialogTitle>{batch ? `Editar ${batch.batch_number}` : "Nuevo Lote de Produccion"}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Horno *</label>
            <SearchableSelect options={ovens.map(o => ({ value: o.id, label: `${o.name} (${o.type}) - ${o.location}` }))}
              value={form.oven_id} onValueChange={selectOven}
              placeholder="Seleccionar horno..." searchPlaceholder="Buscar horno..." testId="batch-oven-select" />
          </div>

          {/* Trazabilidad: Recepciones de MP */}
          <div className="border border-blue-200 rounded-lg p-3 bg-blue-50/30" data-testid="receiving-lots-section">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-blue-800 uppercase tracking-wide">Trazabilidad - Materia Prima</span>
              <select onChange={e => {
                if (!e.target.value) return;
                const recv = availableReceivings.find(r => r.id === e.target.value);
                if (recv && !receivingLots.some(rl => rl.receiving_id === recv.id)) {
                  setReceivingLots(prev => [...prev, {
                    receiving_id: recv.id, folio: recv.folio || "",
                    supplier_name: recv.supplier_name || "", supplier_lot: recv.supplier_lot || "",
                    product_name: recv.product_name || "", qty: recv.real_qty || recv.purchased_qty || 0,
                  }]);
                }
                e.target.value = "";
              }} className="border border-blue-200 rounded px-2 py-1 text-[10px] bg-white" data-testid="add-receiving-lot">
                <option value="">+ Vincular recepcion...</option>
                {suggestedReceivings.filter(r => !receivingLots.some(rl => rl.receiving_id === r.id)).map(r => {
                  const isMatch = matchingIngredientIds.has(r.product_id);
                  return (
                    <option key={r.id} value={r.id}>{isMatch ? "* " : ""}{r.folio || "?"} - {r.supplier_name} - {r.product_name} ({r.real_qty || r.purchased_qty || 0}kg)</option>
                  );
                })}
              </select>
            </div>
            {receivingLots.length > 0 ? (
              <div className="space-y-1">
                {receivingLots.map((rl, i) => (
                  <div key={i} className="bg-white rounded p-2 flex items-center justify-between text-xs border border-blue-100">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-blue-700">{rl.folio}</span>
                      <span className="text-slate-600">{rl.supplier_name}</span>
                      <span className="text-slate-400">Lote: {rl.supplier_lot || "N/A"}</span>
                      <span className="text-slate-600">{rl.product_name}</span>
                      <span className="font-medium">{rl.qty}kg</span>
                    </div>
                    <button type="button" onClick={() => setReceivingLots(prev => prev.filter((_, j) => j !== i))}
                      className="text-red-400 hover:text-red-600 p-0.5">x</button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-blue-400 italic">
                {matchingIngredientIds.size > 0
                  ? "Las recepciones marcadas con * corresponden a la materia prima de este producto."
                  : "Sin recepciones vinculadas. Vincula para trazabilidad SENASICA."}
              </p>
            )}
          </div>

          {/* Time tracking & Gas cylinder */}
          <div className="border border-slate-200 rounded-lg p-3">
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-2">Tiempos y gas</span>
            <div className="grid grid-cols-3 gap-3 mb-2">
              <div>
                <label className="block text-[10px] text-slate-500 mb-0.5">Inicio flama</label>
                <input type="datetime-local" value={form.flame_start || ""} onChange={e => setForm(f => ({ ...f, flame_start: e.target.value }))}
                  className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs" data-testid="flame-start" />
              </div>
              <div>
                <label className="block text-[10px] text-slate-500 mb-0.5">Fin flama (apagar quemador)</label>
                <input type="datetime-local" value={form.flame_end || ""} onChange={e => setForm(f => ({ ...f, flame_end: e.target.value }))}
                  className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs" data-testid="flame-end" />
              </div>
              <div>
                <label className="block text-[10px] text-slate-500 mb-0.5">Fin turbina (apagar todo)</label>
                <input type="datetime-local" value={form.turbine_end || ""} onChange={e => setForm(f => ({ ...f, turbine_end: e.target.value }))}
                  className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs" data-testid="turbine-end" />
              </div>
            </div>
            {form.flame_start && form.flame_end && (() => {
              const fs = new Date(form.flame_start), fe = new Date(form.flame_end);
              const burnerH = ((fe - fs) / 3600000).toFixed(1);
              const te = form.turbine_end ? new Date(form.turbine_end) : null;
              const turbineH = te ? ((te - fs) / 3600000).toFixed(1) : null;
              const coolingH = te ? ((te - fe) / 3600000).toFixed(1) : null;
              return (
                <div className="bg-slate-50 rounded p-2 mb-2 grid grid-cols-3 gap-2 text-xs" data-testid="time-calc">
                  <div className="text-center">
                    <div className="text-[10px] text-slate-500">Quemador</div>
                    <div className="font-bold text-orange-600">{burnerH}h</div>
                  </div>
                  {turbineH && <div className="text-center">
                    <div className="text-[10px] text-slate-500">Turbina total</div>
                    <div className="font-bold text-blue-600">{turbineH}h</div>
                  </div>}
                  {coolingH && <div className="text-center">
                    <div className="text-[10px] text-slate-500">Solo turbina (enfriando)</div>
                    <div className="font-bold text-slate-600">{coolingH}h</div>
                  </div>}
                </div>
              );
            })()}
            {form.oven_id && batch && (
              <div className="mt-2" data-testid="cylinder-usage-section">
                <label className="text-[10px] font-bold text-slate-700 uppercase mb-1.5 block">Cilindros de Gas</label>
                
                {/* Cylinder Slot Panel */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {/* Principal Cylinder */}
                  <div className={`rounded-lg p-2 border ${batch.active_cylinder_id && batch.active_cylinder_id !== batch.auxiliary_cylinder_id ? "border-teal-400 bg-teal-50" : "border-slate-200 bg-slate-50"}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-bold text-teal-700 uppercase">Principal</span>
                      {batch.active_cylinder_id && (
                        <span className="text-[9px] bg-teal-200 text-teal-800 px-1.5 py-0.5 rounded-full font-medium">
                          {batch.active_cylinder_id === batch.auxiliary_cylinder_id ? "" : "Activo"}
                        </span>
                      )}
                    </div>
                    {!batch.active_cylinder_id ? (
                      <select onChange={async e => {
                        if (!e.target.value) return;
                        const cyl = gasCylinders.find(c => c.id === e.target.value);
                        const weight = prompt(`Peso bruto actual de "${cyl?.name}" (kg):`);
                        if (!weight || isNaN(Number(weight))) return;
                        try {
                          await api.post(`/production/batches/${batch.id}/cylinder-switch`, {
                            to_cylinder_id: e.target.value, to_weight_now: Number(weight),
                            switch_time: new Date().toISOString()
                          });
                          toast.success("Cilindro principal conectado");
                          loadBatch(batch.id); loadCylinderUsages(batch.id);
                          if (form.oven_id) api.get(`/gas-cylinders/by-oven/${form.oven_id}`).then(r => setGasCylinders(r.data)).catch(() => {});
                        } catch (err) { toast.error("Error al conectar"); }
                      }} className="w-full border border-slate-200 rounded px-2 py-1 text-[10px]" data-testid="select-principal-cyl">
                        <option value="">Seleccionar cilindro...</option>
                        {gasCylinders.filter(c => c.status !== "agotado").map(c => (
                          <option key={c.id} value={c.id}>{c.name || "?"} ({(c.current_gas || 0).toFixed(1)}kg)</option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-xs text-slate-700 font-medium">
                        {gasCylinders.find(c => c.id === batch.active_cylinder_id)?.name || "Cilindro"}
                        <span className="text-[10px] text-slate-400 ml-1">
                          ({(gasCylinders.find(c => c.id === batch.active_cylinder_id)?.current_gas || 0).toFixed(1)}kg)
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Auxiliary Cylinder */}
                  <div className={`rounded-lg p-2 border ${batch.auxiliary_cylinder_id ? "border-amber-300 bg-amber-50" : "border-dashed border-slate-200 bg-white"}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-bold text-amber-700 uppercase">Auxiliar</span>
                    </div>
                    {!batch.auxiliary_cylinder_id ? (
                      <select onChange={async e => {
                        if (!e.target.value) return;
                        await api.put(`/batches/${batch.id}`, { auxiliary_cylinder_id: e.target.value });
                        toast.success("Cilindro auxiliar asignado");
                        loadBatch(batch.id);
                      }} className="w-full border border-slate-200 rounded px-2 py-1 text-[10px]" data-testid="select-auxiliary-cyl">
                        <option value="">Asignar auxiliar...</option>
                        {gasCylinders.filter(c => c.status !== "agotado" && c.id !== batch.active_cylinder_id).map(c => (
                          <option key={c.id} value={c.id}>{c.name || "?"} ({(c.current_gas || 0).toFixed(1)}kg)</option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-xs text-slate-700 font-medium">
                        {gasCylinders.find(c => c.id === batch.auxiliary_cylinder_id)?.name || "Cilindro"}
                        <span className="text-[10px] text-slate-400 ml-1">
                          ({(gasCylinders.find(c => c.id === batch.auxiliary_cylinder_id)?.current_gas || 0).toFixed(1)}kg)
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Switch Button */}
                {batch.active_cylinder_id && batch.auxiliary_cylinder_id && (
                  <button type="button" data-testid="switch-cylinder-btn" onClick={async () => {
                    const activeCyl = gasCylinders.find(c => c.id === batch.active_cylinder_id);
                    const auxCyl = gasCylinders.find(c => c.id === batch.auxiliary_cylinder_id);
                    const fromWeight = prompt(`Peso bruto actual de "${activeCyl?.name || "principal"}" al desconectar (kg):`);
                    if (!fromWeight || isNaN(Number(fromWeight))) return;
                    const toWeight = prompt(`Peso bruto actual de "${auxCyl?.name || "auxiliar"}" al conectar (kg):`);
                    if (!toWeight || isNaN(Number(toWeight))) return;
                    try {
                      await api.post(`/production/batches/${batch.id}/cylinder-switch`, {
                        from_cylinder_id: batch.active_cylinder_id,
                        to_cylinder_id: batch.auxiliary_cylinder_id,
                        from_weight_now: Number(fromWeight),
                        to_weight_now: Number(toWeight),
                        switch_time: new Date().toISOString()
                      });
                      toast.success("Cilindro cambiado exitosamente");
                      loadBatch(batch.id); loadCylinderUsages(batch.id);
                      if (form.oven_id) api.get(`/gas-cylinders/by-oven/${form.oven_id}`).then(r => setGasCylinders(r.data)).catch(() => {});
                    } catch (err) { toast.error("Error al cambiar cilindro"); }
                  }} className="w-full py-1.5 mb-2 text-[10px] font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors flex items-center justify-center gap-1">
                    Cambiar a {batch.active_cylinder_id === batch.auxiliary_cylinder_id ? "otro" : 
                      gasCylinders.find(c => c.id === batch.auxiliary_cylinder_id)?.name || "auxiliar"}
                  </button>
                )}

                {/* Usage History */}
                {cylinderUsages.length > 0 && (
                  <div className="space-y-1 mb-2">
                    <span className="text-[10px] font-bold text-slate-500 uppercase">Historial de consumo</span>
                    {cylinderUsages.filter(u => u.gas_consumed > 0).map((u, i) => (
                      <div key={u.id || i} className="bg-teal-50 rounded p-2 text-xs grid grid-cols-4 gap-1" data-testid={`cyl-usage-${i}`}>
                        <div><span className="text-[10px] text-teal-600 block">Cilindro</span><span className="font-medium text-slate-700">{u.cylinder_name}</span></div>
                        <div><span className="text-[10px] text-teal-600 block">Antes</span><span className="text-slate-700">{u.weight_before} kg</span></div>
                        <div><span className="text-[10px] text-teal-600 block">Despues</span><span className="text-slate-700">{u.weight_after} kg</span></div>
                        <div><span className="text-[10px] text-teal-600 block">Consumido</span><span className="font-bold text-orange-700">{u.gas_consumed} kg (${u.usage_cost?.toFixed(2) || 0})</span></div>
                      </div>
                    ))}
                    <div className="bg-slate-50 rounded p-2 flex justify-between text-xs font-bold">
                      <span className="text-slate-600">Total gas:</span>
                      <span className="text-orange-700">{cylinderUsages.reduce((s, u) => s + (u.gas_consumed || 0), 0).toFixed(2)} kg</span>
                      <span className="text-slate-800">${cylinderUsages.reduce((s, u) => s + (u.usage_cost || 0), 0).toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* Manual registration fallback */}
                <div className="flex items-center justify-between">
                  {cylinderUsages.length === 0 && !addingCylUsage && (
                    <p className="text-[10px] text-slate-400 italic">Conecta un cilindro principal arriba para empezar.</p>
                  )}
                  <button type="button" onClick={() => setAddingCylUsage(!addingCylUsage)}
                    className="text-[10px] text-teal-600 hover:text-teal-700 font-medium ml-auto" data-testid="add-cyl-usage-btn">
                    {addingCylUsage ? "Cancelar" : "+ Registro manual"}
                  </button>
                </div>
                {addingCylUsage && (
                  <div className="border border-teal-200 rounded-lg p-2 bg-teal-50/50 space-y-2 mt-1" data-testid="cyl-usage-form">
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-[10px] text-slate-500 mb-0.5">Cilindro *</label>
                        <select value={cylUsageForm.cylinder_id} onChange={e => setCylUsageForm(f => ({ ...f, cylinder_id: e.target.value }))}
                          className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs" data-testid="cyl-usage-select">
                          <option value="">Seleccionar...</option>
                          {gasCylinders.filter(c => c.status !== "agotado").map(c => (
                            <option key={c.id} value={c.id}>{c.name || c.purchase_date || "?"} - {(c.current_gas || c.net_gas || 0).toFixed(1)}kg disp.</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] text-slate-500 mb-0.5">Peso antes (kg bruto)</label>
                        <input type="number" value={cylUsageForm.weight_before} onChange={e => setCylUsageForm(f => ({ ...f, weight_before: e.target.value }))}
                          className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs" min="0" step="0.1" placeholder="Al conectar" data-testid="cyl-usage-before" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-slate-500 mb-0.5">Peso despues (kg bruto)</label>
                        <input type="number" value={cylUsageForm.weight_after} onChange={e => setCylUsageForm(f => ({ ...f, weight_after: e.target.value }))}
                          className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs" min="0" step="0.1" placeholder="Al desconectar" data-testid="cyl-usage-after" />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => { setAddingCylUsage(false); setCylUsageForm({ cylinder_id: "", weight_before: "", weight_after: "" }); }}
                        className="px-3 py-1 text-[10px] text-slate-500 hover:bg-slate-100 rounded">Cancelar</button>
                      <button type="button" data-testid="save-cyl-usage-btn" onClick={async () => {
                        if (!cylUsageForm.cylinder_id) { toast.error("Selecciona un cilindro"); return; }
                        if (!cylUsageForm.weight_before || !cylUsageForm.weight_after) { toast.error("Indica peso antes y despues"); return; }
                        try {
                          const res = await api.post(`/production/batches/${batch.id}/cylinder-usage`, {
                            cylinder_id: cylUsageForm.cylinder_id,
                            weight_before: Number(cylUsageForm.weight_before),
                            weight_after: Number(cylUsageForm.weight_after),
                          });
                          setCylinderUsages(prev => [...prev, res.data]);
                          setCylUsageForm({ cylinder_id: "", weight_before: "", weight_after: "" });
                          setAddingCylUsage(false);
                          toast.success(`Gas consumido: ${res.data.gas_consumed} kg ($${res.data.usage_cost?.toFixed(2)})`);
                          if (form.oven_id) api.get(`/gas-cylinders/by-oven/${form.oven_id}`).then(r => setGasCylinders(r.data)).catch(() => {});
                        } catch (err) { toast.error(err.response?.data?.detail || "Error al registrar uso"); }
                      }} className="px-4 py-1 text-[10px] bg-teal-600 hover:bg-teal-700 text-white rounded font-medium">
                        Registrar Uso
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {form.oven_id && !batch && (
              <p className="text-[10px] text-slate-400 italic mt-1">Guarda el lote primero para registrar consumo de gas por cilindro.</p>
            )}
          </div>

          <div className="border border-slate-200 rounded-lg p-3" data-testid="batch-items-section">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Productos en este lote</span>
              <button type="button" onClick={addItem} className="text-xs text-brand-600 hover:text-brand-700 font-medium" data-testid="add-batch-item">+ Agregar producto</button>
            </div>
            {form.items.map((item, idx) => {
              const prod = products.find(p => p.id === item.product_id);
              const hasDiffUnits = prod && prod.purchase_unit && prod.unit && prod.purchase_unit !== prod.unit;
              return (
                <div key={idx} className={`${idx > 0 ? "mt-3 pt-3 border-t border-slate-200" : ""}`} data-testid={`batch-item-${idx}`}>
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4">
                      <label className="block text-[10px] text-slate-500 mb-0.5">Producto *</label>
                      <SearchableSelect options={finalProducts.map(p => ({ value: p.id, label: p.name }))}
                        value={item.product_id} onValueChange={(v) => selectProductForItem(idx, v)}
                        placeholder="Producto..." searchPlaceholder="Buscar..." testId={`item-product-${idx}`} />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[10px] text-slate-500 mb-0.5">Entrada ({item.input_unit})</label>
                      <input type="number" value={item.input_qty || ""} onChange={e => updateItemQty(idx, e.target.value)}
                        className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs" min="0" step="0.1" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[10px] text-slate-500 mb-0.5">Est. salida ({item.output_unit})</label>
                      <input type="number" value={item.estimated_output_qty || ""} onChange={e => updateItem(idx, { estimated_output_qty: Number(e.target.value) })}
                        className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs" min="0" step="0.1" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[10px] text-slate-500 mb-0.5">Horas horno</label>
                      <input type="number" value={item.hours_in_oven || ""} onChange={e => updateItem(idx, { hours_in_oven: Number(e.target.value) || 0 })}
                        placeholder="Ej: 60" className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs" min="0" step="0.5" />
                    </div>
                    {batch && !true && (
                      <div className="col-span-2">
                        <label className="block text-[10px] text-slate-500 mb-0.5">Real ({item.output_unit})</label>
                        <input type="number" value={item.actual_output_qty ?? ""} onChange={e => updateItem(idx, { actual_output_qty: Number(e.target.value) || "" })}
                          className="w-full border border-amber-300 rounded px-2 py-1.5 text-xs" min="0" step="0.1" placeholder="Al completar" />
                      </div>
                    )}
                    {form.items.length > 1 && (
                      <div className={batch ? "col-span-12 flex justify-end -mt-1" : "col-span-2 flex justify-end"}>
                        <button type="button" onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600 text-xs font-medium px-1">Quitar</button>
                      </div>
                    )}
                  </div>
                  {prod && (
                    <div className="text-[10px] text-slate-500 mt-1">
                      Rend: {prod.yield_type === "conteo" ? "conteo (100%)" : `${prod.yield_pct || 0}% peso`}
                      {prod.pieces_per_kg > 0 && <span className="ml-2">| ~{prod.pieces_per_kg} pzas/kg</span>}
                      {prod.cutting_waste_pct > 0 && <span className="ml-2">| Merma corte: {prod.cutting_waste_pct}%</span>}
                      {prod.oven_waste_pct > 0 && <span className="ml-2">| Merma horno: {prod.oven_waste_pct}%</span>}
                    </div>
                  )}
                  {/* Multi-stage waste/yield tracking */}
                  {batch && (
                    <div className="bg-slate-50 border border-slate-200 rounded p-2 mt-1.5" data-testid={`batch-item-${idx}-merma`}>
                      <div className="text-[10px] font-bold text-slate-700 mb-1.5">Control de Mermas y Rendimiento</div>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <label className="block text-[10px] text-slate-500">Peso comprado (kg)</label>
                          <input type="number" value={item.purchased_weight ?? item.input_qty ?? ""} onChange={e => updateItem(idx, { purchased_weight: Number(e.target.value) || 0 })}
                            className="w-full border border-slate-200 rounded px-2 py-1 text-xs" min="0" step="0.1" placeholder={String(item.input_qty || "")} />
                        </div>
                        <div>
                          <label className="block text-[10px] text-slate-500">Peso real s/empaque</label>
                          <input type="number" value={item.real_weight ?? ""} onChange={e => updateItem(idx, { real_weight: Number(e.target.value) || 0 })}
                            className="w-full border border-slate-200 rounded px-2 py-1 text-xs" min="0" step="0.1" placeholder="Sin caja/plastico" />
                        </div>
                        <div>
                          <label className="block text-[10px] text-slate-500">Despues del corte</label>
                          <input type="number" value={item.post_cutting_weight ?? ""} onChange={e => updateItem(idx, { post_cutting_weight: Number(e.target.value) || 0 })}
                            className="w-full border border-slate-200 rounded px-2 py-1 text-xs" min="0" step="0.1" placeholder="Lo que entra al horno" />
                        </div>
                        <div>
                          <label className="block text-[10px] text-slate-500">Salida deshidratado</label>
                          <input type="number" value={item.actual_output_qty ?? ""} onChange={e => updateItem(idx, { actual_output_qty: Number(e.target.value) || "" })}
                            className="w-full border border-amber-300 bg-amber-50 rounded px-2 py-1 text-xs font-medium" min="0" step="0.1" placeholder="Kg finales" />
                        </div>
                      </div>
                      {/* Yield calculations */}
                      {(item.purchased_weight > 0 || item.real_weight > 0 || item.post_cutting_weight > 0 || item.actual_output_qty > 0) && (
                        <div className="mt-2 pt-2 border-t border-slate-200 grid grid-cols-4 gap-2 text-[10px]">
                          {item.purchased_weight > 0 && item.real_weight > 0 && (
                            <div className="text-center">
                              <div className="text-slate-500">Merma empaque</div>
                              <div className="font-bold text-red-600">{((1 - item.real_weight / item.purchased_weight) * 100).toFixed(1)}%</div>
                              <div className="text-slate-400">-{(item.purchased_weight - item.real_weight).toFixed(1)} kg</div>
                            </div>
                          )}
                          {item.real_weight > 0 && item.post_cutting_weight > 0 && (
                            <div className="text-center">
                              <div className="text-slate-500">Merma corte</div>
                              <div className="font-bold text-red-600">{((1 - item.post_cutting_weight / item.real_weight) * 100).toFixed(1)}%</div>
                              <div className="text-slate-400">-{(item.real_weight - item.post_cutting_weight).toFixed(1)} kg</div>
                            </div>
                          )}
                          {item.post_cutting_weight > 0 && item.actual_output_qty > 0 && (
                            <div className="text-center">
                              <div className="text-slate-500">Rend. horno</div>
                              <div className="font-bold text-blue-600">{((item.actual_output_qty / item.post_cutting_weight) * 100).toFixed(1)}%</div>
                              <div className="text-slate-400">{item.post_cutting_weight} &rarr; {item.actual_output_qty} kg</div>
                            </div>
                          )}
                          {item.purchased_weight > 0 && item.actual_output_qty > 0 && (
                            <div className="text-center bg-brand-50 rounded p-1">
                              <div className="text-slate-500">Rend. sobre pagado</div>
                              <div className="font-bold text-brand-700">{((item.actual_output_qty / item.purchased_weight) * 100).toFixed(1)}%</div>
                              <div className="text-slate-400">{item.purchased_weight} pagados &rarr; {item.actual_output_qty} kg</div>
                            </div>
                          )}
                          {(item.post_cutting_weight > 0 || item.real_weight > 0) && item.actual_output_qty > 0 && (
                            <div className="text-center bg-blue-50 rounded p-1">
                              <div className="text-slate-500">Rend. real (neto)</div>
                              <div className="font-bold text-blue-700">{((item.actual_output_qty / (item.post_cutting_weight || item.real_weight)) * 100).toFixed(1)}%</div>
                              <div className="text-slate-400">{item.post_cutting_weight || item.real_weight} netos &rarr; {item.actual_output_qty} kg</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Show required raw materials from recipe */}
                  {prod && (prod.ingredients || []).length > 0 && (item.estimated_output_qty > 0 || item.input_qty > 0) && (
                    <div className="bg-amber-50/50 border border-amber-200 rounded p-2 mt-1.5" data-testid={`batch-item-${idx}-materials`}>
                      <div className="text-[10px] font-bold text-amber-700 mb-1">Materias primas necesarias (segun receta):</div>
                      {prod.ingredients.map((ing, j) => {
                        const estOut = item.estimated_output_qty || 0;
                        const needed = Math.round(estOut * (ing.quantity || 0) * 100) / 100;
                        const ingProduct = products.find(p => p.id === ing.product_id);
                        const stock = ingProduct?.stock || 0;
                        const insufficient = needed > stock;
                        return (
                          <div key={j} className="flex items-center justify-between text-[10px]">
                            <span className="text-slate-600">{ing.product_name || ingProduct?.name || "?"}: <span className="font-medium">{needed} {ing.unit}</span></span>
                            <span className={insufficient ? "text-red-500 font-medium" : "text-emerald-600"}>
                              {insufficient ? `Stock: ${stock} (faltan ${(needed - stock).toFixed(1)})` : `Stock: ${stock} OK`}
                            </span>
                          </div>
                        );
                      })}
                      {prod.ingredients.some(ing => {
                        const needed = (item.estimated_output_qty || 0) * (ing.quantity || 0);
                        const ingProd = products.find(p => p.id === ing.product_id);
                        return needed > (ingProd?.stock || 0);
                      }) && (
                        <div className="text-[10px] text-amber-600 mt-1 italic">Stock insuficiente en algunos insumos. Se permite continuar y ajustar despues.</div>
                      )}
                    </div>
                  )}
                  {/* Output distribution (1-to-many: one raw input → multiple finished products) */}
                  {prod && (prod.output_products || []).length > 0 && batch && (
                    <div className="bg-green-50/50 border border-green-200 rounded p-2 mt-1.5" data-testid={`batch-item-${idx}-distribution`}>
                      <div className="text-[10px] font-bold text-green-700 mb-1">Distribucion de salida</div>
                      <p className="text-[10px] text-slate-400 mb-1.5">Reparte la salida real entre los productos terminados:</p>
                      {(prod.output_products || []).map((op, j) => {
                        const dist = (item.output_distribution || []).find(d => d.product_id === op.product_id) || {};
                        const outProd = products.find(p => p.id === op.product_id);
                        return (
                          <div key={j} className="flex items-center gap-2 mb-1" data-testid={`dist-${idx}-${j}`}>
                            <span className="text-xs text-slate-600 flex-1">{op.product_name || outProd?.name || "?"} ({outProd?.unit || "?"})</span>
                            <input type="number" value={dist.quantity ?? ""} onChange={(e) => {
                              const qty = Number(e.target.value) || 0;
                              const existing = item.output_distribution || [];
                              const updated = existing.filter(d => d.product_id !== op.product_id);
                              updated.push({ product_id: op.product_id, product_name: op.product_name || outProd?.name || "", quantity: qty, unit: outProd?.unit || "pza" });
                              updateItem(idx, { output_distribution: updated });
                            }} placeholder="Cant." min="0" step="0.1"
                              className="w-24 border border-slate-200 rounded px-2 py-1 text-xs text-right" />
                            <span className="text-[10px] text-slate-400">{outProd?.unit || "?"}</span>
                          </div>
                        );
                      })}
                      {item.actual_output_qty > 0 && (
                        <div className="text-[10px] text-slate-500 mt-1 pt-1 border-t border-green-200">
                          Salida real: {item.actual_output_qty} {item.output_unit} |
                          Distribuido: {(item.output_distribution || []).reduce((s, d) => s + (d.quantity || 0), 0).toFixed(1)}
                        </div>
                      )}
                    </div>
                  )}
                  {hasDiffUnits && (
                    <div className="bg-indigo-50/50 border border-indigo-200 rounded p-2 mt-1.5">
                      <div className="text-[10px] font-bold text-indigo-700 mb-1">Conversion: {prod.purchase_unit} &rarr; {prod.unit}</div>
                      <div className="grid grid-cols-4 gap-1.5">
                        <div>
                          <label className="block text-[10px] text-slate-500">Comprado ({prod.purchase_unit})</label>
                          <input type="number" value={item.conversion?.purchased_qty || ""} onChange={e => updateConversion(idx, "purchased_qty", e.target.value)}
                            className="w-full border border-slate-200 rounded px-1.5 py-1 text-xs" min="0" step="0.1" />
                        </div>
                        <div>
                          <label className="block text-[10px] text-slate-500">$/{prod.purchase_unit}</label>
                          <input type="number" value={item.conversion?.purchase_unit_cost || ""} onChange={e => updateConversion(idx, "purchase_unit_cost", e.target.value)}
                            className="w-full border border-slate-200 rounded px-1.5 py-1 text-xs" min="0" step="0.01" />
                        </div>
                        <div>
                          <label className="block text-[10px] text-slate-500">Resulto ({prod.unit})</label>
                          <input type="number" value={item.conversion?.converted_qty || ""} onChange={e => updateConversion(idx, "converted_qty", e.target.value)}
                            className="w-full border border-slate-200 rounded px-1.5 py-1 text-xs" min="0" step="1" />
                        </div>
                        <div className="flex flex-col justify-end text-[10px]">
                          {item.conversion?.purchase_total > 0 && <div className="text-indigo-700 font-semibold">${(item.conversion.purchase_total).toFixed(2)} total</div>}
                          {item.conversion?.cost_per_converted > 0 && <div className="text-indigo-600 font-bold">${(item.conversion.cost_per_converted).toFixed(4)}/{prod.unit}</div>}
                        </div>
                      </div>
                    </div>
                  )}
                  {!hasDiffUnits && showCosts && (
                    <div className="mt-1">
                      <label className="block text-[10px] text-slate-500">Costo materia prima ($)</label>
                      <input type="number" value={item.raw_material_cost || ""} onChange={e => updateItem(idx, { raw_material_cost: Number(e.target.value) || 0 })}
                        className="w-32 border border-slate-200 rounded px-2 py-1 text-xs" min="0" step="0.01" placeholder="0.00" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="border border-slate-200 rounded-lg p-3">
            <div className="flex items-center gap-3 mb-2">
              <input type="checkbox" id="show_batch_costs" checked={showCosts} onChange={e => setShowCosts(e.target.checked)} className="w-3.5 h-3.5 accent-brand-600" />
              <label htmlFor="show_batch_costs" className="text-xs font-bold text-slate-700 uppercase tracking-wide cursor-pointer">Costos del Lote</label>
            </div>
            {showCosts && (
              <div className="space-y-3 mt-2">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-semibold text-slate-600 uppercase">Materiales adicionales</span>
                    <button type="button" onClick={addMaterial} className="text-[10px] text-brand-600 hover:text-brand-700 font-medium">+ Agregar</button>
                  </div>
                  <p className="text-[10px] text-slate-400 mb-1.5">Materiales fisicos extra: vinagre, agua (ingrediente), sal, etc.</p>
                  {(form.batch_costs?.additional_materials || []).map((m, i) => (
                    <div key={i} className="flex items-center gap-1.5 mb-1">
                      <input value={m.name} onChange={e => updateMaterial(i, "name", e.target.value)} placeholder="Vinagre..."
                        className="flex-1 border border-slate-200 rounded px-2 py-1 text-xs" />
                      <input type="number" value={m.qty || ""} onChange={e => updateMaterial(i, "qty", e.target.value)} placeholder="3"
                        className="w-14 border border-slate-200 rounded px-1.5 py-1 text-xs" min="0" step="0.1" />
                      <select value={m.unit} onChange={e => updateMaterial(i, "unit", e.target.value)} className="w-16 border border-slate-200 rounded px-1 py-1 text-xs">
                        {MATERIAL_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                      <span className="text-[10px] text-slate-400">$</span>
                      <input type="number" value={m.unit_cost || ""} onChange={e => updateMaterial(i, "unit_cost", e.target.value)} placeholder="0.00"
                        className="w-16 border border-slate-200 rounded px-1.5 py-1 text-xs" min="0" step="0.01" />
                      <span className="text-xs text-slate-500 w-16 text-right">${(m.total_cost || 0).toFixed(2)}</span>
                      <button type="button" onClick={() => removeMaterial(i)} className="text-red-400 hover:text-red-600 text-xs font-bold">x</button>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-semibold text-slate-600 uppercase">Costos de produccion</span>
                    <button type="button" onClick={addProdCost} className="text-[10px] text-brand-600 hover:text-brand-700 font-medium" data-testid="add-prod-cost">+ Agregar</button>
                  </div>
                  <p className="text-[10px] text-slate-400 mb-1.5">Gas, electricidad, mano de obra, etc. Usa horas x tarifa para calcular automaticamente.</p>
                  {(form.batch_costs?.production_costs || []).length > 0 && (
                    <div className="grid grid-cols-12 gap-1 mb-1 text-[10px] text-slate-400 font-medium px-0.5">
                      <span className="col-span-3">Concepto</span>
                      <span className="col-span-2 text-center">Horas</span>
                      <span className="col-span-2 text-center">$/hora</span>
                      <span className="col-span-3 text-center">Total $</span>
                      <span className="col-span-2"></span>
                    </div>
                  )}
                  {(form.batch_costs?.production_costs || []).map((c, i) => (
                    <div key={i} className="grid grid-cols-12 gap-1 mb-1.5 items-center" data-testid={`prod-cost-${i}`}>
                      <input value={c.name} onChange={e => updateProdCost(i, "name", e.target.value)}
                        placeholder={["Gas del horno", "Electricidad", "Mano de obra", "Empaque", "Agua servicio"][i] || "Concepto..."}
                        className="col-span-3 border border-slate-200 rounded px-2 py-1.5 text-xs" />
                      <input type="number" value={c.hours || ""} onChange={e => updateProdCost(i, "hours", e.target.value)}
                        placeholder="Hrs" min="0" step="0.5"
                        className="col-span-2 border border-slate-200 rounded px-2 py-1.5 text-xs text-center" />
                      <input type="number" value={c.cost_per_hour || ""} onChange={e => updateProdCost(i, "cost_per_hour", e.target.value)}
                        placeholder="$/hr" min="0" step="0.01"
                        className="col-span-2 border border-slate-200 rounded px-2 py-1.5 text-xs text-center" />
                      <div className="col-span-3 flex items-center gap-1">
                        <span className="text-[10px] text-slate-400">=</span>
                        <input type="number" value={c.total || ""} onChange={e => updateProdCost(i, "total", e.target.value)}
                          placeholder="0.00" min="0" step="0.01"
                          className="flex-1 border border-slate-200 rounded px-2 py-1.5 text-xs text-right font-medium" />
                      </div>
                      <div className="col-span-2 flex justify-end">
                        <button type="button" onClick={() => removeProdCost(i)} className="text-red-400 hover:text-red-600 text-xs font-bold px-1">x</button>
                      </div>
                    </div>
                  ))}
                  {(form.batch_costs?.production_costs || []).length === 0 && (
                    <p className="text-[10px] text-slate-400 italic">Sin costos de produccion. Agrega gas, luz, mano de obra, etc.</p>
                  )}
                </div>
                {totalCost > 0 && (
                  <div className="bg-slate-50 rounded-lg p-2 text-xs space-y-1" data-testid="batch-cost-summary">
                    <div className="flex justify-between font-bold text-slate-700">
                      <span>Costo total del lote:</span>
                      <span className="text-brand-700">${totalCost.toFixed(2)}</span>
                    </div>
                    {form.items.length > 1 && totalHours > 0 && (
                      <>
                        <div className="text-[10px] text-slate-500 mt-1">Prorrateo por solapamiento de horas:</div>
                        <table className="w-full text-[10px] mt-0.5">
                          <thead><tr className="text-slate-500">
                            <th className="text-left py-0.5">Producto</th><th className="text-right">Horas</th>
                            <th className="text-right">Compartido</th><th className="text-right">Exclusivo</th>
                            <th className="text-right">MP</th>
                            <th className="text-right font-bold">Total</th>
                            <th className="text-right">$/unidad</th>
                          </tr></thead>
                          <tbody>
                            {form.items.map((it, i) => {
                              const allocated = overlapAllocations ? overlapAllocations[i] : 0;
                              const itemTotal = (it.raw_material_cost || 0) + allocated;
                              const estOut = it.estimated_output_qty || 1;
                              const minHours = Math.min(...form.items.map(x => x.hours_in_oven || 0).filter(h => h > 0));
                              const sharedPart = minHours > 0 ? Math.min(it.hours_in_oven || 0, minHours) : 0;
                              const exclusivePart = Math.max(0, (it.hours_in_oven || 0) - sharedPart);
                              return (
                                <tr key={i} className="border-t border-slate-200">
                                  <td className="py-0.5 font-medium text-slate-700">{it.product_name || `Producto ${i + 1}`}</td>
                                  <td className="text-right">{it.hours_in_oven || 0}h</td>
                                  <td className="text-right text-blue-600">{sharedPart}h</td>
                                  <td className="text-right text-amber-600">{exclusivePart > 0 ? `${exclusivePart}h` : "-"}</td>
                                  <td className="text-right">${(it.raw_material_cost || 0).toFixed(2)}</td>
                                  <td className="text-right font-bold">${itemTotal.toFixed(2)}</td>
                                  <td className="text-right text-brand-700">${(itemTotal / estOut).toFixed(4)}/{it.output_unit}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div className="text-[10px] text-slate-400 mt-1 italic">
                          Horas compartidas = costos divididos entre productos activos. Horas exclusivas = 100% al producto.
                        </div>
                      </>
                    )}
                    {form.items.length === 1 && form.items[0].estimated_output_qty > 0 && (
                      <div className="flex justify-between text-slate-500 mt-0.5">
                        <span>Costo/{form.items[0].output_unit}:</span>
                        <span className="font-semibold">${(totalCost / form.items[0].estimated_output_qty).toFixed(4)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Estimated vs Real Cost Comparison (visible when batch exists) */}
          {batch && showCosts && (form.batch_costs?.production_costs || []).length > 0 && (
            <div className="border border-amber-200 rounded-lg p-3 bg-amber-50/30" data-testid="cost-comparison-section">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold text-amber-800 uppercase tracking-wide">Estimado vs Real</span>
                <span className="text-[10px] text-slate-400">(Ajusta los costos reales al completar el lote)</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] text-slate-500 border-b border-amber-200">
                      <th className="text-left py-1 pr-2">Concepto</th>
                      <th className="text-right py-1 px-2">Estimado ($)</th>
                      <th className="text-right py-1 px-2">Real ($)</th>
                      <th className="text-right py-1 pl-2">Diferencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.batch_costs?.production_costs || []).map((c, i) => {
                      const estimated = c.total || (c.hours || 0) * (c.cost_per_hour || 0);
                      const real = c.real_total ?? null;
                      const diff = real !== null ? real - estimated : null;
                      return (
                        <tr key={i} className="border-b border-amber-100">
                          <td className="py-1.5 pr-2 text-slate-700 font-medium">{c.name || `Costo ${i + 1}`}</td>
                          <td className="py-1.5 px-2 text-right text-slate-500">${estimated.toFixed(2)}</td>
                          <td className="py-1.5 px-2 text-right">
                            <input type="number" value={c.real_total ?? ""} onChange={e => {
                              const upd = [...(form.batch_costs?.production_costs || [])];
                              upd[i] = { ...upd[i], real_total: e.target.value === "" ? null : Number(e.target.value) };
                              setForm(f => ({ ...f, batch_costs: { ...f.batch_costs, production_costs: upd } }));
                            }} placeholder="Real..."
                              className="w-20 border border-amber-200 rounded px-1.5 py-1 text-xs text-right focus:outline-none focus:ring-1 focus:ring-amber-400"
                              data-testid={`real-cost-${i}`} />
                          </td>
                          <td className={`py-1.5 pl-2 text-right font-bold ${diff === null ? "text-slate-300" : diff > 0 ? "text-red-600" : diff < 0 ? "text-green-600" : "text-slate-500"}`}>
                            {diff !== null ? `${diff > 0 ? "+" : ""}$${diff.toFixed(2)}` : "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    {(() => {
                      const totalEst = (form.batch_costs?.production_costs || []).reduce((s, c) => s + (c.total || (c.hours || 0) * (c.cost_per_hour || 0)), 0);
                      const allReal = (form.batch_costs?.production_costs || []).every(c => c.real_total !== null && c.real_total !== undefined);
                      const totalReal = allReal ? (form.batch_costs?.production_costs || []).reduce((s, c) => s + (c.real_total || 0), 0) : null;
                      const totalDiff = totalReal !== null ? totalReal - totalEst : null;
                      return (
                        <tr className="border-t-2 border-amber-300 font-bold">
                          <td className="py-2 pr-2 text-slate-800">TOTAL</td>
                          <td className="py-2 px-2 text-right text-slate-600">${totalEst.toFixed(2)}</td>
                          <td className="py-2 px-2 text-right text-slate-800">{totalReal !== null ? `$${totalReal.toFixed(2)}` : "-"}</td>
                          <td className={`py-2 pl-2 text-right ${totalDiff === null ? "text-slate-300" : totalDiff > 0 ? "text-red-600" : totalDiff < 0 ? "text-green-600" : "text-slate-500"}`}>
                            {totalDiff !== null ? `${totalDiff > 0 ? "+" : ""}$${totalDiff.toFixed(2)}` : "-"}
                          </td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
              <p className="text-[10px] text-amber-600 mt-2 italic">Rojo = gasto mayor al estimado | Verde = ahorraste vs lo estimado</p>
            </div>
          )}

          {batch && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Estado</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="batch-status-select">
                {BATCH_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          )}
          {relevantSales.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-2">Pedidos que cubre ({relevantSales.length})</label>
              <div className="max-h-36 overflow-y-auto space-y-1 bg-slate-50 rounded-lg p-2">
                {relevantSales.map(s => (
                  <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white cursor-pointer text-xs">
                    <input type="checkbox" checked={form.priority_orders.includes(s.id)} onChange={() => toggleOrder(s.id)} className="rounded border-slate-300" />
                    <span className="font-mono font-medium text-slate-700">{s.folio}</span>
                    <span className="text-slate-500">{s.customer_name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notas</label>
            <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Observaciones..." />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
            <button onClick={handleSave} disabled={saving} data-testid="save-batch-btn"
              className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium disabled:opacity-60">
              {saving ? "Guardando..." : batch ? "Actualizar" : "Programar Lote"}
            </button>
          </div>

          {/* Fulfillable Orders Dialog */}
          {fulfillableOrders.length > 0 && (
            <FulfillableOrdersPanel
              orders={fulfillableOrders}
              onMarkReady={handleMarkReady}
              onSkip={() => onSave()}
              marking={markingReady}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── KANBAN CARD ───────────────────────────────────────────────────────────
function KanbanCard({ batch, onEdit, onStatusChange }) {
  const nextStatus = { programado: "en_horno", en_horno: "enfriando", enfriando: null };
  const nextAction = { programado: "Meter al horno", en_horno: "Sacar a enfriar", enfriando: null };
  const next = nextStatus[batch.status];
  const isMixed = batch.is_mixed || (batch.batch_items || []).length > 1;
  const items = batch.batch_items || [];

  return (
    <div className={`bg-white rounded-lg border-l-4 ${batchBorderColor(batch.status)} shadow-sm p-3 hover:shadow-md transition-shadow`}
      data-testid={`kanban-card-${batch.id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-mono font-bold text-slate-600">{batch.batch_number}</span>
        <div className="flex items-center gap-1">
          {isMixed && <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium">Mixto</span>}
          <button onClick={() => onEdit(batch)} className="p-0.5 hover:bg-slate-100 rounded"><Edit size={12} className="text-slate-400" /></button>
        </div>
      </div>
      {isMixed ? (
        <div className="mb-1">
          {items.map((it, i) => (
            <div key={i} className="text-xs text-slate-700">
              <span className="font-medium">{it.product_name}</span>
              <span className="text-slate-400 ml-1">{it.input_qty} {it.input_unit}{it.hours_in_oven ? ` (${it.hours_in_oven}h)` : ""}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm font-semibold text-slate-800 mb-1">{batch.product_name}</div>
      )}
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
        <Flame size={11} className="text-amber-400" />
        <span>{batch.oven_name}</span>
      </div>
      {!isMixed && (
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="text-slate-500">{batch.raw_kg} {items[0]?.input_unit || "kg"} entrada</span>
          <ArrowRight size={10} className="text-slate-300" />
          <span className="font-medium text-slate-700">
            {batch.actual_output_kg ? `${batch.actual_output_kg} ${items[0]?.output_unit || "kg"}` : `~${items[0]?.estimated_output_qty || batch.estimated_output_kg} ${items[0]?.output_unit || "kg"}`}
          </span>
        </div>
      )}
      {batch.total_batch_cost > 0 && (
        <div className="text-[10px] text-emerald-600 mb-1 font-medium">
          Costo lote: ${batch.total_batch_cost}
        </div>
      )}
      {(batch.burner_hours || batch.flame_start) && (
        <div className="flex items-center gap-1.5 text-[10px] text-slate-400 mb-1">
          <Clock size={9} />
          {batch.burner_hours ? <span>Quemador: {batch.burner_hours}h</span> : null}
          {batch.turbine_hours ? <span>| Turbina: {batch.turbine_hours}h</span> : null}
          {!batch.burner_hours && batch.flame_start && <span>Inicio: {new Date(batch.flame_start).toLocaleString("es-MX", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>}
        </div>
      )}
      {batch.priority_orders?.length > 0 && (
        <div className="text-[10px] text-slate-400 mb-2">
          Cubre {batch.priority_orders.length} pedido{batch.priority_orders.length > 1 ? "s" : ""}
        </div>
      )}
      {next && (
        <button onClick={() => onStatusChange(batch.id, next)} data-testid={`kanban-advance-${batch.id}`}
          className={`w-full text-xs font-medium py-1.5 rounded-md transition-colors ${
            next === "en_horno" ? "bg-amber-50 text-amber-700 hover:bg-amber-100" :
            next === "enfriando" ? "bg-cyan-50 text-cyan-700 hover:bg-cyan-100" : ""
          }`}>
          {nextAction[batch.status]}
        </button>
      )}
      {batch.status === "enfriando" && (
        <button onClick={() => onEdit(batch)} data-testid={`kanban-complete-${batch.id}`}
          className="w-full text-xs font-medium py-1.5 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors">
          Completar (registrar salida)
        </button>
      )}
    </div>
  );
}

// ─── MAIN PAGE ─────────────────────────────────────────────────────────────
export default function Fabricacion() {
  const [tab, setTab] = useState("kanban");
  const [planning, setPlanning] = useState(null);
  const [ovens, setOvens] = useState([]);
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [yieldData, setYieldData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOvenForm, setShowOvenForm] = useState(false);
  const [editingOven, setEditingOven] = useState(null);
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [batchPrefill, setBatchPrefill] = useState(null);
  const [expandedDemand, setExpandedDemand] = useState(null);
  const [gasCylinders, setGasCylinders] = useState([]);
  const [showCylinderForm, setShowCylinderForm] = useState(false);
  const [editingCylinder, setEditingCylinder] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [planRes, ovensRes, batchesRes, prodsRes, yieldRes, cylRes] = await Promise.all([
        api.get("/production/planning"),
        api.get("/ovens"),
        api.get("/production/batches"),
        api.get("/products"),
        api.get("/production/yield-analytics").catch(() => ({ data: [] })),
        api.get("/gas-cylinders").catch(() => ({ data: [] })),
      ]);
      setPlanning(planRes.data);
      setOvens(ovensRes.data);
      setBatches(batchesRes.data);
      setProducts(prodsRes.data);
      setYieldData(yieldRes.data);
      setGasCylinders(cylRes.data);
    } catch { toast.error("Error al cargar datos"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteOven = async (id) => {
    if (!window.confirm("Eliminar horno?")) return;
    await api.delete(`/ovens/${id}`); toast.success("Horno eliminado"); load();
  };
  const deleteBatch = async (id) => {
    if (!window.confirm("Eliminar lote?")) return;
    await api.delete(`/production/batches/${id}`); toast.success("Lote eliminado"); load();
  };
  const deleteCylinder = async (id) => {
    if (!window.confirm("Eliminar cilindro de gas?")) return;
    try { await api.delete(`/gas-cylinders/${id}`); toast.success("Cilindro eliminado"); load(); }
    catch { toast.error("Error al eliminar"); }
  };
  const quickStatusBatch = async (batchId, status) => {
    try { await api.put(`/production/batches/${batchId}`, { status }); toast.success(`Lote: ${batchStatusLabel(status)}`); load(); }
    catch { toast.error("Error al actualizar"); }
  };

  const openBatchForm = (productId) => {
    setEditingBatch(null);
    setBatchPrefill(productId ? { product_id: productId } : null);
    setShowBatchForm(true);
  };

  const activeSalesMap = {};
  (planning?.demand_by_product || []).forEach((d) => {
    d.orders.forEach((o) => {
      if (!activeSalesMap[o.sale_id]) activeSalesMap[o.sale_id] = { id: o.sale_id, folio: o.folio, customer_name: o.customer_name, items: [] };
      activeSalesMap[o.sale_id].items.push({ product_id: d.product_id, product_name: d.product_name, quantity: o.qty, unit: d.unit });
    });
  });
  const uniqueSales = Object.values(activeSalesMap);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-brand-600" /></div>;

  const activeBatches = batches.filter((b) => !["completado", "cancelado"].includes(b.status));
  const kanbanColumns = [
    { key: "programado", label: "Programado", icon: "bg-blue-500" },
    { key: "en_horno", label: "En Horno", icon: "bg-amber-500" },
    { key: "enfriando", label: "Enfriando", icon: "bg-cyan-500" },
    { key: "completado", label: "Completado", icon: "bg-emerald-500" },
  ];

  const TABS = [
    { key: "kanban", label: "Kanban", icon: Columns3 },
    { key: "planificacion", label: "Planificacion", icon: ClipboardList },
    { key: "hornos", label: "Hornos y Gas", icon: Flame },
    { key: "rendimiento", label: "Rendimiento", icon: TrendingUp },
  ];

  return (
    <div className="space-y-6" data-testid="fabricacion-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Fabricacion</h1>
          <p className="text-slate-500 text-sm mt-1">Planificacion de produccion, hornos y lotes</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
            <RefreshCw size={14} /> Actualizar
          </button>
          {tab === "hornos" && (
            <>
              <button onClick={() => { setEditingCylinder(null); setShowCylinderForm(true); }} data-testid="new-cylinder-btn"
                className="flex items-center gap-2 border border-teal-300 text-teal-700 hover:bg-teal-50 px-4 py-2 rounded-lg text-sm font-medium">
                <Fuel size={16} /> Nuevo Cilindro
              </button>
              <button onClick={() => { setEditingOven(null); setShowOvenForm(true); }} data-testid="new-oven-btn"
                className="flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
                <Plus size={16} /> Nuevo Horno
              </button>
            </>
          )}
          {["kanban", "planificacion"].includes(tab) && (
            <button onClick={() => openBatchForm(null)} data-testid="new-batch-btn"
              className="flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
              <Plus size={16} /> Nuevo Lote
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
              tab === t.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* ─── TAB: KANBAN ──────────────────────────────────────────────── */}
      {tab === "kanban" && (
        <div className="space-y-4">
          {/* Quick stats */}
          <div className="grid grid-cols-4 gap-3">
            {kanbanColumns.map((col) => {
              const count = batches.filter((b) => b.status === col.key).length;
              return (
                <div key={col.key} className="bg-white rounded-lg border border-slate-200 px-4 py-3 flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${col.icon}`} />
                  <div>
                    <div className="text-lg font-bold text-slate-900">{count}</div>
                    <div className="text-xs text-slate-500">{col.label}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Kanban Board */}
          <div className="grid grid-cols-4 gap-4" data-testid="kanban-board">
            {kanbanColumns.map((col) => {
              const columnBatches = batches.filter((b) => b.status === col.key);
              return (
                <div key={col.key} className="space-y-3" data-testid={`kanban-col-${col.key}`}>
                  <div className="flex items-center gap-2 px-1">
                    <div className={`w-2.5 h-2.5 rounded-full ${col.icon}`} />
                    <span className="text-sm font-semibold text-slate-700">{col.label}</span>
                    <span className="text-xs bg-slate-100 text-slate-500 px-1.5 rounded-full ml-auto">{columnBatches.length}</span>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-2 min-h-[200px] space-y-2">
                    {columnBatches.length === 0 ? (
                      <div className="flex items-center justify-center h-24 text-xs text-slate-400 italic">
                        {col.key === "programado" ? "Programa un lote" : "Sin lotes"}
                      </div>
                    ) : columnBatches.map((b) => (
                      <KanbanCard key={b.id} batch={b}
                        onEdit={(b) => { setEditingBatch(b); setBatchPrefill(null); setShowBatchForm(true); }}
                        onStatusChange={quickStatusBatch} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── TAB: PLANIFICACIÓN ───────────────────────────────────────── */}
      {tab === "planificacion" && planning && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-2xl font-bold text-slate-900">{planning.active_orders_count}</div>
              <div className="text-xs text-slate-500">Pedidos activos</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-2xl font-bold text-slate-900">{planning.demand_by_product?.length || 0}</div>
              <div className="text-xs text-slate-500">Productos a producir</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-2xl font-bold text-slate-900">{ovens.length}</div>
              <div className="text-xs text-slate-500">Hornos configurados</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-2xl font-bold text-amber-600">{activeBatches.length}</div>
              <div className="text-xs text-slate-500">Lotes en proceso</div>
            </div>
          </div>
          {/* Demand queue */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-800">Cola de Produccion (por prioridad)</h2>
              <p className="text-xs text-slate-500 mt-0.5">Ordenado segun pedidos FIFO. Pagados primero.</p>
            </div>
            {!planning.demand_by_product?.length ? (
              <div className="text-center py-12 text-slate-400 text-sm">Sin demanda activa</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {planning.demand_by_product.map((d, idx) => {
                  const matchingOvens = ovens.filter((o) => (o.product_capacities || []).some((c) => c.product_id === d.product_id));
                  const totalCap = matchingOvens.reduce((s, o) => s + ((o.product_capacities || []).find((c) => c.product_id === d.product_id)?.capacity_kg || 0), 0);
                  const cycles = totalCap > 0 ? Math.ceil(d.raw_kg_needed / totalCap) : "?";
                  return (
                    <div key={idx} data-testid={`demand-row-${idx}`}>
                      <button className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 text-left"
                        onClick={() => setExpandedDemand(expandedDemand === idx ? null : idx)}>
                        <div className="flex items-center gap-4">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                            idx === 0 ? "bg-red-100 text-red-700" : idx < 3 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                            {idx + 1}
                          </div>
                          <div>
                            <div className="font-medium text-slate-800">{d.product_name}</div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {d.orders.length} pedidos | <strong>{d.total_qty} {d.unit}</strong>
                              {d.yield_pct > 0 && <span className="text-amber-600"> | Crudo: ~{d.raw_kg_needed} kg (yield {d.yield_pct}%)</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {matchingOvens.length > 0 ? (
                            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                              {matchingOvens.length} horno{matchingOvens.length > 1 ? "s" : ""} | ~{cycles} ciclo{cycles !== 1 ? "s" : ""}
                            </span>
                          ) : (
                            <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">Sin horno</span>
                          )}
                          {expandedDemand === idx ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                        </div>
                      </button>
                      {expandedDemand === idx && (
                        <div className="px-5 pb-4 space-y-3">
                          <div className="bg-slate-50 rounded-lg p-3">
                            <div className="text-xs font-semibold text-slate-600 mb-2">Pedidos</div>
                            {d.orders.map((o, oi) => (
                              <div key={oi} className="flex items-center justify-between text-xs bg-white rounded px-2 py-1.5 mb-1">
                                <span className="w-6 text-center font-bold text-slate-400">#{o.priority}</span>
                                <span className="font-mono font-medium text-slate-700">{o.folio}</span>
                                <span className="text-slate-500 flex-1 mx-3">{o.customer_name}</span>
                                <span className="font-medium">{o.qty} {d.unit}</span>
                                <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  o.status === "pagado" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{o.status}</span>
                              </div>
                            ))}
                          </div>
                          {matchingOvens.length > 0 && (
                            <div className="bg-amber-50 rounded-lg p-3">
                              <div className="text-xs font-semibold text-amber-800 mb-2">Hornos disponibles</div>
                              {matchingOvens.map((o) => {
                                const cap = (o.product_capacities || []).find((c) => c.product_id === d.product_id);
                                return (
                                  <div key={o.id} className="flex items-center justify-between text-xs bg-white rounded px-2 py-1.5 mb-1">
                                    <span className="font-medium text-slate-700">{o.name} ({o.type})</span>
                                    <span className="text-slate-500">{o.location}</span>
                                    <span className="font-semibold text-amber-700">{cap?.capacity_kg || 0} kg</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          <button onClick={() => openBatchForm(d.product_id)} data-testid={`quick-batch-${idx}`}
                            className="text-xs bg-brand-50 text-brand-700 hover:bg-brand-100 px-3 py-1.5 rounded-lg font-medium transition-colors">
                            + Programar lote de {d.product_name}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── TAB: HORNOS ──────────────────────────────────────────────── */}
      {tab === "hornos" && (
        <div className="space-y-4">
          {ovens.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Flame size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm mb-4">Agrega tus hornos y sus capacidades por producto.</p>
              <button onClick={() => { setEditingOven(null); setShowOvenForm(true); }} data-testid="empty-new-oven-btn"
                className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium">
                <Plus size={14} className="inline mr-1" /> Agregar primer horno
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ovens.map((oven) => {
                const activeLotesInOven = activeBatches.filter((b) => b.oven_id === oven.id);
                const ovenCylinders = gasCylinders.filter(c => c.oven_id === oven.id);
                const activeCyl = ovenCylinders.find(c => c.status === "lleno" || c.status === "en_uso" || c.status === "activo");
                return (
                  <div key={oven.id} className={`bg-white rounded-xl border p-5 ${
                    activeLotesInOven.length > 0 ? "border-amber-200 ring-1 ring-amber-100" : "border-slate-200"
                  }`} data-testid={`oven-card-${oven.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Flame size={18} className={oven.type === "grande" ? "text-orange-500" : "text-amber-400"} />
                        <div>
                          <div className="font-semibold text-slate-800">{oven.name}</div>
                          <div className="text-xs text-slate-500">{oven.type === "grande" ? "Grande" : "Chico"} | {oven.location}</div>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {activeLotesInOven.length > 0 && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium mr-1">
                            En uso
                          </span>
                        )}
                        <button onClick={() => { setEditingOven(oven); setShowOvenForm(true); }}
                          className="p-1.5 hover:bg-slate-100 rounded-md"><Edit size={14} className="text-slate-500" /></button>
                        <button onClick={() => deleteOven(oven.id)}
                          className="p-1.5 hover:bg-red-50 rounded-md"><Trash2 size={14} className="text-red-400" /></button>
                      </div>
                    </div>
                    {(oven.product_capacities || []).length > 0 ? (
                      <div className="space-y-1 mb-2">
                        <div className="text-xs font-semibold text-slate-500 mb-1">Capacidades (kg crudo)</div>
                        {oven.product_capacities.map((cap, i) => (
                          <div key={i} className="flex justify-between text-xs bg-slate-50 rounded px-2 py-1.5">
                            <span className="text-slate-700">{cap.product_name || "Producto"}</span>
                            <span className="font-bold text-slate-800">{cap.capacity_kg} kg</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic mb-2">Sin capacidades configuradas</p>
                    )}
                    {/* Gas Cylinder section per oven */}
                    <div className="border-t border-slate-100 pt-2 mt-2">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <Fuel size={13} className="text-teal-500" />
                          <span className="text-xs font-semibold text-slate-600">Cilindro de gas</span>
                        </div>
                        <button onClick={() => { setEditingCylinder(null); setShowCylinderForm(true); }}
                          className="text-[10px] text-teal-600 hover:text-teal-700 font-medium" data-testid={`add-cyl-${oven.id}`}>
                          + Nuevo
                        </button>
                      </div>
                      {activeCyl ? (
                        <div className="bg-teal-50 rounded-lg p-2.5" data-testid={`active-cyl-${oven.id}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-teal-800">{activeCyl.name || "Cilindro"} - {(activeCyl.current_gas || activeCyl.net_gas || 0).toFixed(1)}kg</span>
                            <div className="flex items-center gap-1">
                              <button onClick={() => { setEditingCylinder(activeCyl); setShowCylinderForm(true); }}
                                className="p-0.5 hover:bg-teal-100 rounded"><Edit size={11} className="text-teal-600" /></button>
                              <button onClick={() => deleteCylinder(activeCyl.id)}
                                className="p-0.5 hover:bg-red-100 rounded"><Trash2 size={11} className="text-red-400" /></button>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-1 text-[10px]">
                            <div><span className="text-teal-600">Tara:</span> <span className="font-medium text-slate-700">{activeCyl.tare_weight || 0} kg</span></div>
                            <div><span className="text-teal-600">Gas disp:</span> <span className="font-bold text-slate-800">{(activeCyl.current_gas || 0).toFixed(1)} kg</span></div>
                            <div><span className="text-teal-600">Costo:</span> <span className="font-medium text-slate-700">${(activeCyl.total_cost || 0).toFixed(2)}</span></div>
                          </div>
                          {activeCyl.net_gas > 0 && (
                            <div className="mt-1.5">
                              <div className="w-full h-1.5 bg-teal-200 rounded-full overflow-hidden">
                                <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${Math.min(100, ((activeCyl.current_gas || 0) / activeCyl.net_gas) * 100)}%` }} />
                              </div>
                              <div className="text-[9px] text-teal-600 mt-0.5">{Math.round(((activeCyl.current_gas || 0) / activeCyl.net_gas) * 100)}% restante</div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-[10px] text-slate-400 italic">Sin cilindro activo</p>
                      )}
                      {ovenCylinders.filter(c => c.status === "agotado").length > 0 && (
                        <details className="mt-1.5">
                          <summary className="text-[10px] text-slate-400 cursor-pointer hover:text-slate-500">
                            {ovenCylinders.filter(c => c.status === "agotado").length} cilindro(s) agotado(s)
                          </summary>
                          <div className="space-y-1 mt-1">
                            {ovenCylinders.filter(c => c.status === "agotado").map(c => (
                              <div key={c.id} className="flex items-center justify-between text-[10px] bg-slate-50 rounded px-2 py-1">
                                <span className="text-slate-500">{c.name || "Cilindro"} - {c.net_gas || 0}kg - {c.purchase_date || "?"}</span>
                                <div className="flex items-center gap-1">
                                  <span className="text-slate-400 bg-slate-100 px-1 rounded">agotado</span>
                                  <button onClick={() => { setEditingCylinder(c); setShowCylinderForm(true); }}
                                    className="p-0.5 hover:bg-slate-200 rounded"><Edit size={10} className="text-slate-400" /></button>
                                  <button onClick={() => deleteCylinder(c.id)}
                                    className="p-0.5 hover:bg-red-100 rounded"><Trash2 size={10} className="text-red-300" /></button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                    {activeLotesInOven.length > 0 && (
                      <div className="bg-amber-50 rounded-lg p-2 mt-2">
                        <div className="text-xs font-semibold text-amber-700 mb-1">Lotes activos en este horno</div>
                        {activeLotesInOven.map((b) => (
                          <div key={b.id} className="text-xs flex justify-between text-amber-800">
                            <span>{b.batch_number}: {b.product_name}</span>
                            <span className={`px-1.5 py-0.5 rounded font-medium ${batchStatusColor(b.status)}`}>{batchStatusLabel(b.status)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {oven.notes && <p className="text-xs text-slate-500 mt-2 italic">{oven.notes}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── TAB: RENDIMIENTO ─────────────────────────────────────────── */}
      {tab === "rendimiento" && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-800">Rendimiento Real por Producto</h2>
              <p className="text-xs text-slate-500 mt-0.5">Basado en lotes completados. Compara yield real vs configurado.</p>
            </div>
            {yieldData.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">
                Completa lotes con kg reales para ver metricas de rendimiento
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {yieldData.map((y, i) => {
                  const prod = products.find((p) => p.id === y.product_id);
                  const configuredYield = prod?.yield_pct || 0;
                  const diff = configuredYield > 0 ? y.avg_real_yield_pct - configuredYield : 0;
                  return (
                    <div key={i} className="px-5 py-4" data-testid={`yield-row-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-medium text-slate-800">{y.product_name}</div>
                          <div className="text-xs text-slate-500">{y.batches_count} lotes completados | {y.total_raw_kg} kg procesados</div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-slate-900">{y.avg_real_yield_pct}%</div>
                          <div className="text-xs text-slate-500">yield real</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-3 mt-3">
                        <div className="bg-slate-50 rounded-lg p-2 text-center">
                          <div className="text-xs text-slate-500">Configurado</div>
                          <div className="text-sm font-bold text-slate-700">{configuredYield}%</div>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2 text-center">
                          <div className="text-xs text-slate-500">Real promedio</div>
                          <div className="text-sm font-bold text-slate-700">{y.avg_real_yield_pct}%</div>
                        </div>
                        <div className={`rounded-lg p-2 text-center ${diff >= 0 ? "bg-emerald-50" : "bg-red-50"}`}>
                          <div className="text-xs text-slate-500">Diferencia</div>
                          <div className={`text-sm font-bold ${diff >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                            {diff >= 0 ? "+" : ""}{diff.toFixed(1)}%
                          </div>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2 text-center">
                          <div className="text-xs text-slate-500">Eficiencia</div>
                          <div className={`text-sm font-bold ${y.efficiency_pct >= 95 ? "text-emerald-700" : y.efficiency_pct >= 85 ? "text-amber-700" : "text-red-700"}`}>
                            {y.efficiency_pct}%
                          </div>
                        </div>
                      </div>
                      {/* Yield bar */}
                      <div className="mt-3">
                        <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                          <span>0%</span>
                          {configuredYield > 0 && <span style={{marginLeft: `${configuredYield}%`}}>Configurado ({configuredYield}%)</span>}
                          <span>100%</span>
                        </div>
                        <div className="relative w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${diff >= 0 ? "bg-emerald-500" : "bg-amber-500"}`}
                            style={{ width: `${Math.min(100, y.avg_real_yield_pct)}%` }} />
                          {configuredYield > 0 && (
                            <div className="absolute top-0 h-full w-0.5 bg-slate-400"
                              style={{ left: `${configuredYield}%` }} />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {showOvenForm && <OvenForm oven={editingOven} products={products} onClose={() => setShowOvenForm(false)} onSave={() => { setShowOvenForm(false); load(); }} />}
      {showBatchForm && <BatchForm batch={editingBatch} ovens={ovens} products={products} activeSales={uniqueSales}
        prefill={batchPrefill} onClose={() => setShowBatchForm(false)} onSave={() => { setShowBatchForm(false); load(); }} />}
      {showCylinderForm && <GasCylinderForm cylinder={editingCylinder} ovens={ovens}
        onClose={() => setShowCylinderForm(false)} onSave={() => { setShowCylinderForm(false); load(); }} />}
    </div>
  );
}
